import React from 'react';

import { ActionButtonVariants } from 'components/action-button';
import LoadingAnimation from 'components/loading-animation';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';

import buttonColor from './button-color';
import { NextIcon } from './icons';
import {
  AddressStyle,
  BadgeContent,
  BottomServiceWrapper,
  ButtonContainer,
  Container,
  Details,
  Heading,
  HeadingDetailsWrapper,
  IconContainer,
  LocationOverlayButton,
  LocationStyle,
  OrderStatusText,
  StyledButton,
  Underline,
} from './styled';
import theme from './theme';
import { BottomServiceVariants, IBottomServiceModeViewProps } from './types';

export const BottomServiceModeView = React.forwardRef<HTMLDivElement, IBottomServiceModeViewProps>(
  (
    {
      isLoading = false,
      variant = BottomServiceVariants.DEFAULT,
      heading,
      details,
      icon,
      buttonBadgeContentText,
      buttonContentText,
      buttonContentTextLabel,
      buttonContentIcon,
      onBottomServiceClick = () => {},
      onButtonClick = () => {},
      buttonDisabled = false,
    },
    ref
  ) => {
    const mParticle = useMParticleContext();
    if (isLoading) {
      const isInProgress = variant === BottomServiceVariants.IN_PROGRESS;
      return (
        <Container variant={variant} ref={ref}>
          <LoadingAnimation reversed={isInProgress} />
        </Container>
      );
    }

    let buttonVariant: ActionButtonVariants;
    switch (variant) {
      case BottomServiceVariants.IN_PROGRESS:
      case BottomServiceVariants.IN_RESTAURANT_LOYALTY:
        buttonVariant = theme.inProgressVariant;
        break;
      case BottomServiceVariants.NO_STORE:
      case BottomServiceVariants.DEFAULT:
      default:
        buttonVariant = theme.defaultButtonVariant;
    }
    const isStatusUpdated = variant !== BottomServiceVariants.IN_PROGRESS;

    const color = buttonColor(variant, true);
    const bottomServiceButtonId = 'bottom-service-button';
    const bottomServiceHeadingId = 'bottom-service-heading';
    const bottomServiceDetailsId = 'bottom-service-details';

    const handleCartClick = () => {
      mParticle.logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          text: 'View Cart',
        },
      });
      onButtonClick();
    };

    return (
      <Container variant={variant} ref={ref} testID="bottom-service">
        <BottomServiceWrapper>
          <LocationOverlayButton
            onPress={onBottomServiceClick}
            testID="bottom-service-location"
            aria-labelledby={`${bottomServiceHeadingId} ${bottomServiceDetailsId}`}
          >
            <IconContainer variant={variant}>{icon}</IconContainer>
            <HeadingDetailsWrapper>
              <Heading testID={bottomServiceHeadingId} variant={variant}>
                {heading}
              </Heading>
              <Details testID={bottomServiceDetailsId}>
                <LocationStyle>
                  <AddressStyle testID="bottom-service-address" data-private>
                    <OrderStatusText variant={variant}>{details}</OrderStatusText>
                  </AddressStyle>
                  {isStatusUpdated && <NextIcon />}
                </LocationStyle>
                {isStatusUpdated && <Underline />}
              </Details>
            </HeadingDetailsWrapper>
          </LocationOverlayButton>
          {buttonContentText && (
            <ButtonContainer>
              <StyledButton
                onPress={handleCartClick}
                variant={buttonVariant}
                color={color}
                testID={bottomServiceButtonId}
                disabled={buttonDisabled}
                // @ts-expect-error TS(2769) FIXME: No overload matches this call.
                startIcon={buttonContentIcon}
                accessibilityLabel={buttonContentTextLabel || buttonContentText}
              >
                {buttonContentText}
              </StyledButton>
              {!!buttonBadgeContentText && <BadgeContent>{buttonBadgeContentText}</BadgeContent>}
            </ButtonContainer>
          )}
        </BottomServiceWrapper>
      </Container>
    );
  }
);
